import { Badge, Popper } from "@mui/material";
import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from "features/config/hooks";
import moment from "moment";
import { useState, MouseEvent, useCallback, useEffect } from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { MdOutlineNotificationsNone } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import {
  StockOrderBreadCrumbsProps,
  StockOrderBreadCrumbs,
} from "./stock-order-breakcrumbs";
import { ProcessModal } from "../modals/process.modal";
import {
  selectStockUserNotification,
  stockUserNotification,
} from "../slices/user-notification.slice";
import {
  selectUpdateNotificationSeen,
  updateNotificationSeen,
  updateNotificationSeenState,
} from "../slices/update-notification-seen.slice";

interface StockOrderHeadHeadProps {
  StockOrderBreadCrumbsProps: StockOrderBreadCrumbsProps;
}

interface NotificationLinkProps {
  onLinkClicked: () => void;
  notificationMessage: string;
  timestamp: string;
  link: string;
  seen: string;
}

export function StockOrderHead(props: StockOrderHeadHeadProps) {
  const { orderId, view } = useParams();

  const query = useQuery();
  const tabValue = query.get("tab");

  const userNotificationState = useAppSelector(selectStockUserNotification);
  const notificationSeenState = useAppSelector(selectUpdateNotificationSeen);

  const { notification_count, notifications } = userNotificationState?.data ?? {
    notification_count: 0,
    notifications: [],
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openProcess, setOpenProcess] = useState(false);
  const dispatch = useAppDispatch();

  const open = Boolean(anchorEl);
  const id = open ? "simple-popper" : undefined;

  useEffect(() => {
    if (notificationSeenState.status === updateNotificationSeenState.success) {
      dispatch(stockUserNotification());
    }
  }, [notificationSeenState]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleLinkClick = useCallback(
    (id: string, seen: string) => () => {
      setAnchorEl(null);
      setOpenProcess(true);

      if (seen === null) {
        dispatch(updateNotificationSeen({ notificationId: id }));
      }
    },
    []
  );

  return (
    <>
      <div className="flex justify-between p-4">
        <StockOrderBreadCrumbs {...props.StockOrderBreadCrumbsProps} />

        <button aria-describedby={id} type="button" onClick={handleClick}>
          <Badge badgeContent={notification_count} color="primary">
            <MdOutlineNotificationsNone className="cursor-pointer " size={20} />
          </Badge>
        </button>
      </div>
      <Popper sx={{ zIndex: 40 }} id={id} open={open} anchorEl={anchorEl}>
        <div className="z-40 mr-4 lg:mr-6 lg:block bg-paper shadow-2xl">
          <div className="h-[600px] w-[300px] lg:h-[400px] lg:max-w-[400px] lg:min-w-[300px] lg:overflow-y-auto bg-color-paper">
            <div className="bg-secondary font-bold text-white text-center text-xl w-100% p-2">
              Notifications
            </div>

            {notification_count !== 0 ? (
              notifications?.map((notif) => (
                <NotificationLink
                  key={notif.id}
                  onLinkClicked={handleLinkClick(notif.id, notif.seen)}
                  timestamp={notif.date}
                  seen={notif.seen}
                  notificationMessage={
                    notif.target_component !== null
                      ? notif.title + " from " + notif.from
                      : notif.title
                  }
                  link={`/admin/material/${view}/${notif.order_id}?tab=${
                    notif.process_id - 1
                  }${
                    notif.target_component !== null
                      ? "&target=" + notif.target_component
                      : ""
                  }`}
                />
              ))
            ) : (
              <span className="flex justify-center p-3">No notification/s</span>
            )}
          </div>
        </div>
      </Popper>

      <ProcessModal
        open={openProcess}
        onClose={() => setOpenProcess(false)}
        scrollTarget="commentElement"
      />
    </>
  );
}

/**
 * Separated component for Notidication Link/Navigation Only
 * @param props - see notification link props
 * @returns single navigation/link element
 */
function NotificationLink(props: NotificationLinkProps) {
  const { onLinkClicked, notificationMessage, timestamp, link, seen } = props;

  return (
    <Link
      className={`flex py-2 items-center px-3 space-x-2 mb-1 ${
        seen === null ? " bg-gray-200" : ""
      }`}
      onClick={() => {
        onLinkClicked();
      }}
      to={link}
    >
      <FaExclamationCircle className="text-4xl text-green-700" />
      <div className="flex flex-col justify-start">
        <span className="text-sm font-semibold">{notificationMessage}</span>
        <span className="text-xs">
          {moment(timestamp).startOf("hour").fromNow()}
        </span>
      </div>
    </Link>
  );
}
